import { useRouter } from 'next/router';
import { Hits, useSearchBox } from 'react-instantsearch';
import { useState, useEffect, CSSProperties } from 'react';
import { useTranslation } from 'next-i18next';
import translationFallback from '@/utilites/translations/translationFallback';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { useBlockScroll } from '@/hooks/useBlockScroll';
import SearchResultsHitMobile from '../searchResultsHitMobile';
import handleSearchQuery from '@/utilites/algolia/handle-search-query';
import handleKeyDown from '@/utilites/algolia/handle-key-down';
import InstantSearchAlgolia from '../InstantSearchAlgolia';
import { SearchInGuidesProps } from '@/types/props/components';
import AlgoliaSearchBar from './AlgoliaSearch/AlgoliaSearchBar';
import clsx from 'clsx';
import { createSearchUrl } from '@/utilites/algolia/create-algolia-urls';
import isStringDefined from '@/utilites/is-string-defined';
import SpiritoryInput from '@/components/common/Forms/SpiritoryInput';
import { IoCloseOutline } from 'react-icons/io5';

export default function SearchInGuides({
  becomeCallToAct,
  becomeTitle,
  styling,
  userId,
}: SearchInGuidesProps) {
  const router = useRouter();
  const [searchQuery, setSearchQuery] = useState('');
  const {
    bgColor: bgColorProp = null,
    bTitleColor: bTitleColorProp = null,
    bCallToActColor: bCallToActColorProp = null,
  } = styling || {};
  const [showResult, setShowResult] = useState(false);
  const [mobileModal, setMobileModal] = useState(false);
  const { t: tCommon } = useTranslation('common');

  const containerClassName = clsx(
    'tw-h-auto tw-w-4/5 tw-mx-auto tw-rounded-2xl d-flex justify-content-between align-items-center px-5 px-sm-6 py-5 max-[1181px]:tw-flex max-[1181px]:tw-flex-col max-[1181px]:tw-items-center max-[1181px]:tw-gap-4 max-[1181px]:tw-text-center max-nine:tw-flex-col',
    {
      'tw-bg-[#EDEDEE]': bgColorProp === null,
    }
  );

  const becomeTitleClassName = clsx('tw-font-medium tw-text-xl', {
    'tw-text-main': bTitleColorProp === null,
  });

  const bCallToActClassName = clsx('tw-text-3xl tw-font-bold', {
    'tw-text-black': bCallToActColorProp === null,
  });

  const containerStyles: CSSProperties = {
    ...(bgColorProp !== null && { backgroundColor: bgColorProp }),
  };

  const bTitleStyles: CSSProperties = {
    ...(bTitleColorProp !== null && { color: bTitleColorProp }),
  };

  const bCallToActStyles: CSSProperties = {
    ...(bCallToActColorProp !== null && { color: bCallToActColorProp }),
  };

  useBlockScroll(mobileModal);

  useEffect(() => {
    if (!mobileModal) {
      setShowResult(false);
    }
  }, [mobileModal]);

  const showMobileModal = () => setMobileModal(true);

  const saveSearchQuery = (query: string) => setSearchQuery(query);

  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isStringDefined(searchQuery)) {
      router.push(createSearchUrl(searchQuery));
    }
  };

  return (
    <>
      <InstantSearchAlgolia>
        {/* DESKTOP COMPONENT */}
        <div className="max-nine:tw-hidden nine:tw-contents">
          <div className="tw-mx-sm-6 tw-mx-0 tw-my-0 tw-my-9 tw-flex max-nine:!tw-hidden">
            <div className={containerClassName} style={containerStyles}>
              <div className="tw-flex tw-basis-2/4 tw-flex-col tw-text-left max-xl:tw-text-center">
                <p className={becomeTitleClassName} style={bTitleStyles}>
                  {becomeTitle}
                </p>
                <h2 className={bCallToActClassName} style={bCallToActStyles}>
                  {becomeCallToAct}
                </h2>
              </div>
              <form
                className="tw-flex tw-h-12 tw-flex-shrink tw-flex-grow-0 tw-basis-2/4 tw-flex-nowrap tw-items-stretch tw-px-1 max-[1181px]:tw-w-full sm:tw-px-0"
                onSubmit={handleFormSubmit}
              >
                <div className="tw-relative tw-h-full tw-w-full">
                  <AlgoliaSearchBar
                    userId={userId}
                    className="tw-h-full"
                    saveSearchQuery={saveSearchQuery}
                    inputClassName="border-searchbox shadow-none !tw-rounded-tl-xl !tw-rounded-bl-xl tw-h-full"
                    searchContainerClassName="tw-h-full tw-flex child:tw-h-full child:child:!tw-h-full"
                  />
                </div>
              </form>
            </div>
          </div>
        </div>
        {/* MOBILE COMPONENT */}
        <div className="nine:tw-hidden">
          <div className="tw-mx-0 tw-my-9 tw-flex sm:tw-mx-6 nine:!tw-hidden">
            <div className={containerClassName} style={containerStyles}>
              <div className="tw-flex tw-basis-2/4 tw-flex-col tw-text-center">
                <p className={becomeTitleClassName} style={bTitleStyles}>
                  {becomeTitle}
                </p>
                <h2 className={bCallToActClassName} style={bCallToActStyles}>
                  {becomeCallToAct}
                </h2>
              </div>
              <div className="tw-flex tw-h-9 tw-w-full tw-flex-nowrap tw-items-center tw-px-1 sm:tw-px-0">
                <div className="tw-relative tw-h-full tw-w-full">
                  <SpiritoryInput
                    onClick={showMobileModal}
                    className="tw-cursor-pointer !tw-bg-gray-50"
                    placeholder={translationFallback('search', tCommon('search'), 'Search')}
                    readOnly
                    searchIcon
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="tw-h-full nine:!tw-hidden">
            <div
              className={clsx(
                'tw-fixed tw-left-0 tw-top-0 tw-h-screen tw-w-screen tw-transform tw-overflow-y-scroll tw-bg-white tw-pb-16 tw-transition-transform tw-duration-150 tw-ease-in-out',
                mobileModal ? 'tw-z-[9999] tw-scale-100' : 'tw-z-0 tw-scale-0'
              )}
            >
              <div className="tw-relative tw-flex tw-w-full tw-items-center tw-justify-center tw-p-3 tw-text-base tw-text-black">
                <button
                  className="tw-absolute tw-left-0 tw-top-1/2 tw--translate-y-1/2 tw-border-none tw-bg-transparent tw-text-[#000]"
                  aria-label="Close search"
                  onClick={() => setMobileModal(false)}
                >
                  <FontAwesomeIcon icon={faChevronLeft} width={15} height={15} />
                </button>
                <span>
                  <b>{translationFallback('search', tCommon('search'), 'Search')}</b>
                </span>
              </div>
              <div className="tw-px-3">
                <div className="tw-z-50 tw-w-full">
                  <SearchInGuidesMobileAlgoliaSearch
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    setShowResult={setShowResult}
                    handleSearchQuery={handleSearchQuery}
                    tCommon={tCommon}
                  />
                </div>
                <div className="tw-flex tw-flex-col tw-items-start tw-pt-6 tw-text-black">
                  {showResult && (
                    <>
                      <b>{translationFallback('products', tCommon('products'), 'Products')}</b>
                      <div className="tw-w-full">
                        <Hits
                          hitComponent={SearchResultsHitMobile}
                          classNames={{
                            item: 'tw-py-3 tw-px-0 tw-shadow-none tw-w-full',
                            list: 'tw-w-full tw-bg-white tw-relative tw-z-50 tw-list-none tw-p-0',
                          }}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </InstantSearchAlgolia>
    </>
  );
}
const SearchInGuidesMobileAlgoliaSearch = ({
  searchQuery,
  setSearchQuery,
  setShowResult,
  handleSearchQuery,
  tCommon,
}: {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  setShowResult: React.Dispatch<React.SetStateAction<boolean>>;
  handleSearchQuery: (
    query: string,
    setQuery: (query: string) => void,
    setShow: React.Dispatch<React.SetStateAction<boolean>>
  ) => void;
  tCommon: (key: string) => string;
}) => {
  const router = useRouter();
  const { refine } = useSearchBox();
  return (
    <SpiritoryInput
      searchIcon
      value={searchQuery}
      onChange={e => {
        const query = e.target.value;
        setSearchQuery(query);
        handleSearchQuery(query, setSearchQuery, setShowResult);
        refine(query);
      }}
      placeholder={`${translationFallback(
        'search_something',
        tCommon('search_something'),
        'Search something here'
      )}...`}
      onKeyDown={event => handleKeyDown(event, router.push, setShowResult)}
      className="tw-rounded-xl"
      rightIcon={
        searchQuery ? (
          <IoCloseOutline
            className="tw-cursor-pointer"
            onClick={() => {
              setSearchQuery('');
              setShowResult(false);
            }}
          />
        ) : undefined
      }
    />
  );
};
